import { FlexColumn, FlexRow, LabeledInput, StyledButton, useLoading, useModal } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { HeadingSmall, ParagraphMedium } from 'baseui/typography';
import { useEffect } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { FENCE_TYPES } from '../../constants/fences';
import { DRAW_MODE_POLYGON } from '../../constants/map';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { deleteFence, getFence, postFence, updateFence } from '../../services/fenceService';
import { apiParamsState } from '../../storage/app';
import { editingFenceState, selectedFenceIdState, selectedFenceState, updateFencesState } from '../../storage/fences';
import { mapDrawModeState } from '../../storage/map';
import { Fence } from '../../types/fence';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import { AssetListPicker } from '../shared/assetListPicker/AssetListPicker';
import ConfirmModal from '../shared/ConfirmModal';
import LocalizedDatePicker from '../shared/LocalizedDatePicker';
import { LabeledSelect } from '../ui/LabeledSelect';

export function FenceDetail() {
  const { theme, css } = useStyles();
  const [selectedFenceId, setSelectedFenceId] = useRecoilState(selectedFenceIdState);
  const resetSelectedFenceId = useResetRecoilState(selectedFenceIdState);
  const [fence, setFence] = useRecoilState(selectedFenceState);
  const apiParams = useRecoilValue(apiParamsState);
  const [editingFence, setEditingFence] = useRecoilState(editingFenceState);
  const resetEditingFence = useResetRecoilState(editingFenceState);
  const setDrawMode = useSetRecoilState(mapDrawModeState);
  const { openModal, closeModal, showModal } = useModal();
  const setUpdateFences = useSetRecoilState(updateFencesState);
  const { loading, startLoading, stopLoading } = useLoading();
  const isNewFence = editingFence?._id === '';
  const { showFailFeedback, showDeletionFeedback, showPositiveFeedback } = useFeedback();

  const loadFenceData = async () => {
    if (!selectedFenceId || !apiParams) return;
    startLoading();
    const { fence, error } = await getFence(selectedFenceId, apiParams.clientId, apiParams.subClientId);
    if (!error && fence) {
      setEditingFence(fence);
      setFence(fence);
    } else {
      // TODO error manager
    }
    stopLoading();
  };

  useEffect(() => {
    if (selectedFenceId) {
      loadFenceData();
    }

    return () => {
      resetEditingFence();
      setFence(undefined);
    };
  }, [selectedFenceId]);

  if (editingFence === undefined) return null;

  const canSubmit =
    !!editingFence.name &&
    (!editingFence.indeterminate ? !!editingFence.startDate && !!editingFence.endDate : true) &&
    !!editingFence.polygon &&
    !!editingFence.assignedAssets.length;
  const hasUpdates = !areEqual(fence || {}, editingFence);

  const onDeleteFence = async () => {
    if (!apiParams || selectedFenceId === undefined) return;
    const { error } = await deleteFence(apiParams.clientId, apiParams.subClientId, selectedFenceId);
    if (!error) {
      showDeletionFeedback(FEEDBACK.deleted(FEEDBACK_PREFIXES.geofence, fence?.name));
      setUpdateFences('update');
      resetSelectedFenceId();
      setFence(undefined);
      closeModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedDeletion(FEEDBACK_PREFIXES.geofence, fence?.name));
    }
  };

  const onInputChange = (value: string | boolean | number | null, field: keyof Fence) => {
    setEditingFence((prev) => ({ ...prev!, [field]: value }));
  };

  const onSaveFence = async () => {
    startLoading();
    if (!apiParams) return;
    const { id, error } = isNewFence
      ? await postFence(apiParams.clientId, apiParams.subClientId, editingFence)
      : await updateFence(apiParams.clientId, apiParams.subClientId, editingFence);
    if (!error && id) {
      if (isNewFence) setSelectedFenceId(id);
      showPositiveFeedback(
        isNewFence
          ? FEEDBACK.created(FEEDBACK_PREFIXES.geofence)
          : FEEDBACK.edited(FEEDBACK_PREFIXES.geofence, editingFence.name)
      );
      setUpdateFences(id);
      setFence(editingFence);
      setDrawMode(DRAW_MODE_POLYGON);
    } else {
      showFailFeedback(
        error || isNewFence
          ? FEEDBACK.failedCreation(FEEDBACK_PREFIXES.geofence)
          : FEEDBACK.failedEdition(FEEDBACK_PREFIXES.geofence, fence?.name)
      );
    }
    stopLoading();
  };

  const onAssetsSelectionChange = (assetIds: string[]) => {
    setEditingFence((prev) => ({ ...prev!, assignedAssets: assetIds }));
  };

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale600}
    >
      <HeadingSmall margin={0}>{isNewFence ? 'Nuevo geocerco' : fence?.name}</HeadingSmall>
      <FlexColumn classNames={css({ flex: 1 })}>
        <LabeledInput
          label="Nombre"
          value={editingFence.name}
          onChange={(value) => onInputChange(value, 'name')}
          required
        />
        <LabeledSelect
          label="Tipo"
          options={FENCE_TYPES}
          value={[{ type: editingFence.fenceType }]}
          labelKey="name"
          valueKey="type"
          onChange={(params) => onInputChange(params.option?.type, 'fenceType')}
          maxWidth={200}
          placeholder="Selecciona un tipo"
        />
        <Checkbox
          checked={editingFence.active}
          onChange={(e) => onInputChange(e.target.checked, 'active')}
        >
          Activo
        </Checkbox>
        <Checkbox
          checked={editingFence.indeterminate}
          onChange={(e) => {
            onInputChange(e.target.checked, 'indeterminate');
            if (e.target.checked) {
              onInputChange(null, 'startDate');
              onInputChange(null, 'endDate');
            }
          }}
        >
          Siempre
        </Checkbox>
        {!editingFence.indeterminate && (
          <LocalizedDatePicker
            value={[
              editingFence.startDate ? new Date(editingFence.startDate) : undefined,
              editingFence.endDate ? new Date(editingFence.endDate) : undefined,
            ]}
            startDateLabel="Fecha de inicio *"
            endDateLabel="Fecha de termino *"
            required
            size="compact"
            onClose={() => {}}
            onChange={({ date }) => {
              if (date instanceof Array) {
                if (date.length > 1 && date[0] && date[1]) {
                  onInputChange(date[0].getTime(), 'startDate');
                  onInputChange(date[1].getTime(), 'endDate');
                } else if (date[0]) {
                  onInputChange(date[0].getTime(), 'startDate');
                  onInputChange(null, 'endDate');
                }
              }
            }}
            range
            separateRangeInputs
          />
        )}
        <AssetListPicker
          key={editingFence._id}
          assignedAssetIds={editingFence.assignedAssets ?? []}
          onAssignedChange={onAssetsSelectionChange}
          required
        />
      </FlexColumn>
      <FlexRow
        classNames={`${css({ alignItems: 'center', justifyContent: isNewFence ? 'flex-end' : 'space-between' })}`}
      >
        {!isNewFence && (
          <StyledButton
            kind="tertiary"
            onClick={openModal}
          >
            Eliminar
          </StyledButton>
        )}
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            kind="tertiary"
            onClick={() => {
              setFence(undefined);
              resetSelectedFenceId();
            }}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={onSaveFence}
            isLoading={loading}
            disabled={!canSubmit || !hasUpdates}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onDeleteFence}
        confirmText="Eliminar"
        description={<ParagraphMedium>¿Desea eliminar "{fence?.name}"?</ParagraphMedium>}
      />
    </FloatingCardContent>
  );
}
